import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";

import { LOREN_ROUTES } from "./shared/constants/loren-routes.constants";
import { LOREN_ROUTER_OUTLETS } from "./shared/constants/loren-router-outlets.constants";
import { LorenAdminGuard } from "./features/loren-admin/shared/guards/loren-admin-guard/loren-admin.guard";
import { LorenDashboardFeatureToggleGuard } from "./shared/guards/dashboard-feature-toggle-guard/loren-dashboard-feature-toggle.guard";
import { LorenTracesFeatureToggleGuard } from "./shared/guards/traces-feature-toggle-guard/loren-traces-feature-toggle.guard";
import { DevicesFeatureToggleGuard } from "./shared/guards/devices-feature-toggle-guard/devices-feature-toggle.guard";
import { LorenAuthenticationGuard } from "./shared/guards/authentication-guard/loren-authentication-guard.service";
import { LorenLoginModule } from "./features/loren-login/loren-login.module";
import { LorenHomeModule } from "./features/loren-home/loren-home.module";
import { LorenMapModule } from "./features/loren-map/loren-map.module";
import { LorenDashboardModule } from "./features/loren-dashboard/loren-dashboard.module";
import { LorenAssetDetailsModule } from "./features/loren-asset-details/loren-asset-details.module";
import { LorenTracesModule } from "./features/loren-traces/loren-traces.module";
import { LorenGeofencingModule } from "./features/loren-geofencing/loren-geofencing.module";
import { LorenAssetsModule } from "./features/loren-assets/loren-assets.module";
import { LorenGroupsModule } from "./features/loren-groups/loren-groups.module";
import { DevicesModule } from "./features/loren-devices/devices.module";
import { LorenAssetsReportsModule } from "./features/loren-assets-reports/loren-assets-reports.module";
import { LorenAutomationPlansModule } from "./features/loren-automation-plans/loren-automation-plans.module";
import { LorenNotificationModule } from "./features/loren-notification/loren-notification.module";
import { LorenNotificationPanelModule } from "./features/loren-notification-panel/loren-notification-panel.module";
import { LorenUserProfileModule } from "./features/loren-user-profile/loren-user-profile.module";
import { LorenUserSettingsModule } from "./features/loren-user-settings/loren-user-settings.module";
import { LorenFaqModule } from "./features/loren-faq/loren-faq.module";
import { LorenUserAccessModule } from "./features/loren-user-access/loren-user-access.module";
import { LorenRolesModule } from "./features/loren-roles/loren-roles.module";
import { LorenRoleFormModule } from "./features/loren-role-form/loren-role-form.module";
import {
  LorenErrorNotFoundComponent
} from "./shared/modules/loren-error/components/loren-error-not-found/loren-error-not-found.component";
import { LorenPasswordResetModule } from "./features/loren-password-reset/loren-password-reset.module";
import { LorenForgottenPasswordModule } from "./features/loren-forgotten-password/loren-forgotten-password.module";

const routes: Routes = [
  {
    path: "",
    redirectTo: LOREN_ROUTES.LOGIN,
    pathMatch: "full",
  },
  {
    path: LOREN_ROUTES.LOGIN,
    loadChildren: () => LorenLoginModule,
  },
  {
    path: LOREN_ROUTES.ADMIN,
    canActivate: [LorenAdminGuard],
    loadChildren: () => import("./features/loren-admin/loren-admin.module").then((m) => m.LorenAdminModule),
  },
  {
    path: "forgotten-password",
    loadChildren: () => LorenForgottenPasswordModule,
  },
  {
    // https://github.com/angular/angular/issues/10981
    path: "reset-password/:token",
    loadChildren: () => LorenPasswordResetModule,
  },
  {
    path: "",
    canActivate: [LorenAuthenticationGuard],
    children: [
      {
        path: LOREN_ROUTES.DASHBOARD,
        loadChildren: () => LorenHomeModule,
      },
      {
        path: LOREN_ROUTES.MAP,
        loadChildren: () => LorenMapModule,
      },
      {
        path: LOREN_ROUTES.ASSETS_LIST,
        canActivate: [LorenDashboardFeatureToggleGuard],
        loadChildren: () => LorenDashboardModule,
      },
      {
        path: LOREN_ROUTES.ASSET,
        // canActivate: guard for roles
        loadChildren: () => LorenAssetDetailsModule,
      },
      {
        path: LOREN_ROUTES.TRACES,
        canActivate: [LorenTracesFeatureToggleGuard],
        loadChildren: () => LorenTracesModule,
      },
      {
        path: LOREN_ROUTES.GEOFENCING,
        loadChildren: () => LorenGeofencingModule,
      },
      {
        path: LOREN_ROUTES.ASSETS,
        loadChildren: () => LorenAssetsModule,
        outlet: LOREN_ROUTER_OUTLETS.DETAILS,
      },
      {
        path: LOREN_ROUTES.GROUPS,
        loadChildren: () => LorenGroupsModule,
      },
      {
        path: LOREN_ROUTES.DEVICES,
        canActivate: [DevicesFeatureToggleGuard],
        loadChildren: () => DevicesModule,
      },
      {
        path: LOREN_ROUTES.REPORTS,
        loadChildren: () => LorenAssetsReportsModule,
      },
      {
        path: LOREN_ROUTES.PLANS,
        loadChildren: () => LorenAutomationPlansModule,
      },
      {
        path: LOREN_ROUTES.NOTIFICATIONS,
        loadChildren: () => LorenNotificationModule,
      },
      {
        path: LOREN_ROUTES.NOTIFICATION_PANEL,
        outlet: LOREN_ROUTER_OUTLETS.DETAILS,
        loadChildren: () => LorenNotificationPanelModule,
      },
      {
        path: LOREN_ROUTES.USER,
        loadChildren: () => LorenUserProfileModule,
      },
      {
        path: LOREN_ROUTES.NOTIFICATIONS_SETTINGS,
        loadChildren: () => LorenUserSettingsModule,
      },
      {
        path: LOREN_ROUTES.FAQ,
        loadChildren: () => LorenFaqModule,
      },
      {
        path: LOREN_ROUTES.USER_ACCESS,
        loadChildren: () => LorenUserAccessModule,
      },
      {
        path: LOREN_ROUTES.ROLES_FORM,
        outlet: LOREN_ROUTER_OUTLETS.DETAILS,
        loadChildren: () => LorenRoleFormModule,
      },
      {
        path: LOREN_ROUTES.ROLES,
        loadChildren: () => LorenRolesModule,
      },
      {
        path: "**",
        loadComponent: () => LorenErrorNotFoundComponent,
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
